<template>
  <div>
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-1">
      <div class="d-flex align-items-center">
        <div class="avatar bg-light-primary mr-1 p-50 rounded">
          <feather-icon
            icon="BarChart2Icon"
            size="20"
            class="text-primary"
          />
        </div>
        <div>
          <h4 class="mb-25 font-weight-bolder">
            {{ $t("Subject-wise Attendance Summary") }}
          </h4>
          <p class="text-muted mb-0 small">
            {{
              $t(
                "Filter and view student attendance statistics across subjects"
              )
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- Filters Card -->
    <b-card no-body class="border mb-2">
      <div class="p-1">
        <b-row>
          <b-col md="3" sm="6">
            <b-form-group :label="$t('Date From') + ' *'" class="mb-1">
              <flat-pickr
                v-model="otherReportObj.dateFrom"
                :config="config"
                class="form-control"
                :placeholder="$t('Select Date')"
              />
            </b-form-group>
          </b-col>
          <b-col md="3" sm="6">
            <b-form-group :label="$t('Date Till') + ' *'" class="mb-1">
              <flat-pickr
                v-model="otherReportObj.dateTill"
                :config="config"
                class="form-control"
                :placeholder="$t('Select Date')"
              />
            </b-form-group>
          </b-col>
          <b-col md="3" sm="6">
            <b-form-group :label="$t(getLabel('Class'))" class="mb-1">
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="otherReportObj.classIDs"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="modalClassOptions"
                :reduce="(val) => val.id"
                label="name"
                @input="onModalClassChange()"
                :placeholder="$t('Select ' + getLabel('class'))"
              />
            </b-form-group>
          </b-col>
          <b-col md="3" sm="6">
            <b-form-group :label="$t(getLabel('Section'))" class="mb-1">
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="otherReportObj.sectionIDs"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="modalSectionOptions"
                :reduce="(val) => val.id"
                label="section"
                :placeholder="$t('Select ' + getLabel('section'))"
              />
            </b-form-group>
          </b-col>
          <b-col md="4" sm="6">
            <b-form-group :label="$t('Subjects')" class="mb-1 mb-md-0">
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="otherReportObj.subjectIDs"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="modalSubjectOptions"
                :reduce="(val) => val.id"
                label="subject"
                :placeholder="$t('Select subjects')"
              />
            </b-form-group>
          </b-col>
          <b-col md="4" sm="6">
            <b-form-group :label="$t('Student')" class="mb-1 mb-md-0">
              <v-select
                v-model="otherReportObj.studentID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="searchedStudentOptions"
                :reduce="(val) => val.id"
                label="label"
                :filterable="false"
                :loading="studentSearchLoading"
                @search="onStatsStudentSearch"
                :placeholder="$t('Search and select student...')"
              >
                <template #option="item">
                  <div class="d-flex flex-column py-25">
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <span class="font-weight-bold">{{ item.name }}</span>
                    </div>
                    <small class="text-muted">
                      <span v-if="item.className">{{ item.className }}</span>
                      <span v-if="item.sectionName">
                        • {{ item.sectionName }}</span
                      >
                      <span v-if="item.father_name">
                        • Father: {{ item.father_name }}</span
                      >
                    </small>
                  </div>
                </template>
                <template #selected-option="item">
                  <div class="d-flex align-items-center">
                    <span>{{ item.name }}</span>
                    <small v-if="item.className" class="text-muted ml-50"
                      >- {{ item.className }}</small
                    >
                  </div>
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            md="4"
            sm="12"
            class="d-flex align-items-end justify-content-end"
          >
            <b-button
              variant="outline-secondary"
              class="mr-1"
              @click="resetStatsFilter()"
              :disabled="statsLoading"
            >
              <feather-icon icon="RotateCcwIcon" class="mr-50" />
              {{ $t("Reset") }}
            </b-button>
            <b-button
              variant="primary"
              @click="loadAttendanceStats()"
              :disabled="statsLoading"
            >
              <b-spinner
                v-if="statsLoading"
                small
                type="grow"
                class="mr-50"
              />
              <feather-icon v-else icon="FilterIcon" class="mr-50" />
              {{ $t("Apply Filter") }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Status Legend & Search Bar -->
    <div v-if="otherReportItems && otherReportItems.length" class="mb-1">
      <b-row class="align-items-center">
        <b-col md="6" class="mb-1 mb-md-0">
          <b-form-input
            v-model="statsSearchQuery"
            :placeholder="
              $t('Search student by name, father, GR#, or class...')
            "
            class="stats-search-input"
          />
        </b-col>
        <b-col md="6">
          <div
            class="d-flex align-items-center justify-content-md-end flex-wrap"
            style="gap: 6px"
          >
            <b-badge
              variant="light-success"
              class="d-inline-flex align-items-center py-50 px-75"
            >
              <span class="bullet bullet-sm bullet-success mr-50" />
              {{ $t("Present") }}
            </b-badge>
            <b-badge
              variant="light-warning"
              class="d-inline-flex align-items-center py-50 px-75"
              style="
                color: #b78103;
                background-color: rgba(255, 193, 7, 0.15);
              "
            >
              <span
                class="bullet bullet-sm mr-50"
                style="background-color: #ffc107"
              />
              {{ $t("Late") }}
            </b-badge>
            <b-badge
              variant="light-warning"
              class="d-inline-flex align-items-center py-50 px-75"
            >
              <span class="bullet bullet-sm bullet-warning mr-50" />
              {{ $t("Very Late") }}
            </b-badge>
            <b-badge
              variant="light-danger"
              class="d-inline-flex align-items-center py-50 px-75"
            >
              <span class="bullet bullet-sm bullet-danger mr-50" />
              {{ $t("Absent") }}
            </b-badge>
            <b-badge
              variant="light-info"
              class="d-inline-flex align-items-center py-50 px-75"
            >
              <span class="bullet bullet-sm bullet-info mr-50" />
              {{ $t("Excused") }}
            </b-badge>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Loading State -->
    <div v-if="statsLoading" class="text-center my-4 py-3">
      <b-spinner
        variant="primary"
        style="width: 3.5rem; height: 3.5rem"
        type="grow"
      />
      <h5 class="text-muted mt-2 font-weight-normal">
        {{ $t("Loading attendance statistics...") }}
      </h5>
    </div>

    <!-- Initial / Empty State -->
    <div
      v-else-if="
        !statsApplied && (!otherReportItems || !otherReportItems.length)
      "
      class="text-center my-4 py-3 text-muted"
    >
      <div
        class="avatar bg-light-primary p-1 mb-1 rounded-circle"
        style="
          width: 64px;
          height: 64px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        "
      >
        <feather-icon icon="FilterIcon" size="32" class="text-primary" />
      </div>
      <h5 class="font-weight-bolder text-dark mb-50">
        {{ $t("No Filters Applied Yet") }}
      </h5>
      <p class="text-muted small mb-0">
        {{
          $t(
            "Select the required date range and filters above, then click Apply Filter to generate the summary."
          )
        }}
      </p>
    </div>

    <!-- No records after filter -->
    <div
      v-else-if="
        statsApplied &&
        (!filteredStatsStudents || !filteredStatsStudents.length)
      "
      class="text-center my-4 py-3 text-muted"
    >
      <div
        class="avatar bg-light-secondary p-1 mb-1 rounded-circle"
        style="
          width: 64px;
          height: 64px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        "
      >
        <feather-icon icon="InboxIcon" size="32" class="text-secondary" />
      </div>
      <h5 class="font-weight-bolder text-dark mb-50">
        {{ $t("No Student Records Found") }}
      </h5>
      <p class="text-muted small mb-0">
        {{
          $t(
            "Try adjusting your date range or class/section/subject filters."
          )
        }}
      </p>
    </div>

    <!-- Students List View -->
    <div v-else class="attendance-stats-list">
      <b-card
        v-for="std in filteredStatsStudents"
        :key="std.sID || std.grNo"
        no-body
        class="border mb-1 shadow-sm student-stats-card"
      >
        <div
          class="p-1 border-bottom bg-white d-flex flex-wrap justify-content-between align-items-center"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              :src="std.picture"
              size="46"
              variant="light-primary"
              class="mr-1 shadow-xs"
            >
              <feather-icon v-if="!std.picture" icon="UserIcon" size="20" />
            </b-avatar>
            <div>
              <div
                class="d-flex align-items-center flex-wrap"
                style="gap: 6px"
              >
                <h5 class="mb-0 font-weight-bolder text-dark">
                  {{ std.name || "-" }}
                </h5>
                <b-badge
                  variant="light-info"
                  pill
                  class="px-75 py-25 font-small-2"
                >
                  {{ std.className || "-" }}
                  <span v-if="std.sectionName">• {{ std.sectionName }}</span>
                </b-badge>
              </div>
              <small v-if="std.father_name" class="text-muted d-block mt-25">
                {{ $t("Father") }}: {{ std.father_name }}
              </small>
            </div>
          </div>
        </div>

        <!-- Subject Breakdown Table -->
        <div class="table-responsive mb-50">
          <table
            class="table table-sm table-hover mb-0 text-center font-small-3"
          >
            <thead class="thead-light">
              <tr>
                <th class="text-left pl-1" style="min-width: 140px">
                  {{ $t("Subject") }}
                </th>
                <th style="min-width: 60px">
                  <span class="text-success font-weight-bolder">P</span>
                </th>
                <th style="min-width: 60px">
                  <span class="text-warning font-weight-bolder">{{
                    $t("Late")
                  }}</span>
                </th>
                <th style="min-width: 65px">
                  <span style="color: #ff9f43" class="font-weight-bolder">{{
                    $t("Very Late")
                  }}</span>
                </th>
                <th style="min-width: 60px">
                  <span class="text-danger font-weight-bolder">A</span>
                </th>
                <th style="min-width: 60px">
                  <span class="text-info font-weight-bolder">E</span>
                </th>
                <th style="min-width: 70px">{{ $t("Total") }}</th>
                <th style="min-width: 130px">{{ $t("Percentage") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="sub in std.subjects"
                :key="sub.subID || sub.subject"
                style="cursor: pointer"
                @click="showAttDetails(std, sub)"
              >
                <td
                  class="text-left pl-1 font-weight-bold text-dark align-middle"
                >
                  {{ sub.subject || "-" }}
                </td>
                <td class="align-middle">
                  <b-badge
                    variant="light-success"
                    pill
                    class="px-75 py-25 font-small-2"
                  >
                    {{ sub.p || sub.P || 0 }}
                  </b-badge>
                </td>
                <td class="align-middle">
                  <b-badge
                    variant="light-warning"
                    pill
                    class="px-75 py-25 font-small-2"
                    style="
                      color: #b78103;
                      background-color: rgba(255, 193, 7, 0.15);
                    "
                  >
                    {{ sub.late || sub.Late || 0 }}
                  </b-badge>
                </td>
                <td class="align-middle">
                  <b-badge
                    variant="light-warning"
                    pill
                    class="px-75 py-25 font-small-2"
                  >
                    {{ sub.veryLate || sub.verylate || sub.VeryLate || 0 }}
                  </b-badge>
                </td>
                <td class="align-middle">
                  <b-badge
                    variant="light-danger"
                    pill
                    class="px-75 py-25 font-small-2"
                  >
                    {{ sub.a || sub.A || 0 }}
                  </b-badge>
                </td>
                <td class="align-middle">
                  <b-badge
                    variant="light-info"
                    pill
                    class="px-75 py-25 font-small-2"
                  >
                    {{ sub.l || sub.L || 0 }}
                  </b-badge>
                </td>
                <td class="align-middle font-weight-bolder text-dark">
                  {{ sub.total || sub.Total || 0 }}
                </td>
                <td class="align-middle font-weight-bolder text-dark">
                  {{ sub.percentage || sub.Percentage || 0 }}%
                </td>
              </tr>
              <tr
                v-if="std.subjects && std.subjects.length"
                class="font-weight-bolder bg-light"
                style="cursor: pointer"
                @click="showAttDetails(std, null)"
              >
                <td
                  class="text-left pl-1 font-weight-bolder text-dark align-middle"
                >
                  {{ $t("Total") }}
                </td>
                <td class="align-middle">
                  <b-badge
                    variant="light-success"
                    pill
                    class="px-75 py-25 font-small-2"
                  >
                    {{ std.p || std.P || 0 }}
                  </b-badge>
                </td>
                <td class="align-middle">
                  <b-badge
                    variant="light-warning"
                    pill
                    class="px-75 py-25 font-small-2"
                    style="
                      color: #b78103;
                      background-color: rgba(255, 193, 7, 0.15);
                    "
                  >
                    {{ std.late || std.Late || 0 }}
                  </b-badge>
                </td>
                <td class="align-middle">
                  <b-badge
                    variant="light-warning"
                    pill
                    class="px-75 py-25 font-small-2"
                  >
                    {{ std.veryLate || std.verylate || std.VeryLate || 0 }}
                  </b-badge>
                </td>
                <td class="align-middle">
                  <b-badge
                    variant="light-danger"
                    pill
                    class="px-75 py-25 font-small-2"
                  >
                    {{ std.a || std.A || 0 }}
                  </b-badge>
                </td>
                <td class="align-middle">
                  <b-badge
                    variant="light-info"
                    pill
                    class="px-75 py-25 font-small-2"
                  >
                    {{ std.l || std.L || 0 }}
                  </b-badge>
                </td>
                <td class="align-middle font-weight-bolder text-dark">
                  {{ std.total || std.Total || 0 }}
                </td>
                <td class="align-middle font-weight-bolder text-dark">
                  {{ std.percentage || std.Percentage || 0 }}%
                </td>
              </tr>
              <tr v-if="!std.subjects || !std.subjects.length">
                <td
                  colspan="8"
                  class="text-center text-muted py-1 font-small-2"
                >
                  {{
                    $t("No subject attendance records found for this student")
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>

      <!-- Showing count at bottom -->
      <div
        v-if="filteredStatsStudents && filteredStatsStudents.length"
        class="d-flex justify-content-between align-items-center mt-1 px-50"
      >
        <span class="text-muted font-small-2">
          {{ $t("Showing") }}
          <strong>{{ filteredStatsStudents.length }}</strong>
          {{ $t("student(s)") }}
        </span>
      </div>
    </div>

    <!-- Attendance Details Modal -->
    <b-modal
      id="modal-att-details"
      :title="$t('Attendance Details')"
      centered
      size="lg"
      footer-class="justify-content-between"
      body-class="p-1"
    >
      <div v-if="studentDetailsLoading" class="text-center py-3">
        <b-spinner variant="primary" label="Loading..." />
        <p class="text-muted mt-50 mb-0 font-small-3">
          {{ $t("Loading attendance details...") }}
        </p>
      </div>

      <div
        v-else-if="studentDetailsList && studentDetailsList.length"
        class="attendance-details-container"
      >
        <div
          v-for="stdItem in studentDetailsList"
          :key="stdItem.sID"
          class="mb-1"
        >
          <!-- Student Info Header -->
          <div
            class="p-1 rounded bg-light border-bottom mb-1 d-flex flex-wrap justify-content-between align-items-center"
          >
            <div class="d-flex align-items-center">
              <b-avatar
                :src="stdItem.picture"
                size="46"
                variant="light-primary"
                class="mr-1 shadow-xs"
              >
                <feather-icon
                  v-if="!stdItem.picture"
                  icon="UserIcon"
                  size="20"
                />
              </b-avatar>
              <div>
                <div
                  class="d-flex align-items-center flex-wrap"
                  style="gap: 6px"
                >
                  <h5 class="mb-0 font-weight-bolder text-dark">
                    {{ stdItem.name || "-" }}
                  </h5>
                  <b-badge
                    v-if="stdItem.grNo"
                    variant="light-primary"
                    pill
                    class="px-75 py-25 font-small-2"
                  >
                    GR# {{ stdItem.grNo }}
                  </b-badge>
                  <b-badge
                    v-if="stdItem.className || stdItem.sectionName"
                    variant="light-info"
                    pill
                    class="px-75 py-25 font-small-2"
                  >
                    {{ stdItem.className || "-" }}
                    <span v-if="stdItem.sectionName"
                      >• {{ stdItem.sectionName }}</span
                    >
                  </b-badge>
                </div>
                <small
                  v-if="stdItem.father_name"
                  class="text-muted d-block mt-25"
                >
                  {{ $t("Father") }}: {{ stdItem.father_name }}
                </small>
              </div>
            </div>
          </div>

          <!-- Attendance Records Table -->
          <div
            class="table-responsive"
            style="max-height: 420px; overflow-y: auto"
          >
            <table
              class="table table-sm table-hover mb-0 text-center font-small-3"
            >
              <thead
                class="thead-light"
                style="position: sticky; top: 0; z-index: 1"
              >
                <tr>
                  <th style="width: 50px">#</th>
                  <th class="text-left pl-1" style="min-width: 130px">
                    {{ $t("Date") }}
                  </th>
                  <th class="text-left pl-1" style="min-width: 150px">
                    {{ $t("Subject") }}
                  </th>
                  <th style="min-width: 90px">
                    {{ $t("Status") }}
                  </th>
                  <th class="text-left pl-1" style="min-width: 150px">
                    {{ $t("Reason") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(rec, rIdx) in stdItem.attendance"
                  :key="rec.id || rIdx"
                >
                  <td class="align-middle">{{ rIdx + 1 }}</td>
                  <td
                    class="align-middle text-left pl-1 font-weight-bold text-dark"
                  >
                    {{
                      rec.date
                        ? new Date(rec.date).toLocaleDateString("en-UK", {
                            year: "numeric",
                            day: "numeric",
                            month: "short",
                          })
                        : "-"
                    }}
                  </td>
                  <td class="align-middle text-left pl-1">
                    {{ rec.subject || rec.subjectName || "-" }}
                  </td>
                  <td class="align-middle">
                    <b-badge
                      :variant="getAttStatusVariant(rec.status)"
                      :style="getAttStatusStyle(rec.status)"
                      pill
                      class="px-75 py-25 font-small-2 font-weight-bolder"
                    >
                      {{ $t(formatAttStatus(rec.status)) }}
                    </b-badge>
                  </td>
                  <td class="align-middle text-left pl-1">
                    {{ rec.meta_notes || "-" }}
                  </td>
                </tr>
                <tr
                  v-if="!stdItem.attendance || !stdItem.attendance.length"
                >
                  <td
                    colspan="5"
                    class="text-center text-muted py-2 font-small-2"
                  >
                    {{ $t("No attendance records found") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-3 text-muted">
        <feather-icon icon="InboxIcon" size="32" class="mb-50 text-muted" />
        <p class="mb-0 font-small-3">
          {{ $t("No attendance records found") }}
        </p>
      </div>

      <template #modal-footer="{ close }">
        <div
          class="d-flex justify-content-between align-items-center w-100"
        >
          <span class="text-muted font-small-2">
            <template
              v-if="studentDetailsList && studentDetailsList.length"
            >
              {{ $t("Total Records") }}:
              <strong>
                {{
                  studentDetailsList.reduce(
                    (acc, s) =>
                      acc + ((s.attendance && s.attendance.length) || 0),
                    0
                  )
                }}
              </strong>
            </template>
          </span>
          <b-button variant="secondary" size="sm" @click="close()">
            {{ $t("Close") }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BAvatar,
  BCol,
  BFormInput,
  BButton,
  BBadge,
  BModal,
  BFormGroup,
  BSpinner,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions } from "vuex";

export default {
  name: "AttendanceSummary",
  components: {
    BCard,
    BRow,
    BAvatar,
    BBadge,
    BCol,
    BFormInput,
    BButton,
    BFormGroup,
    BModal,
    BSpinner,
    vSelect,
    flatPickr,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      classes: [],
      subjects: [],
      otherReportObj: {
        dateFrom: "",
        dateTill: "",
        classIDs: [],
        sectionIDs: [],
        subjectIDs: [],
        studentID: null,
      },
      searchedStudentOptions: [],
      studentSearchLoading: false,
      otherReportItems: [],
      statsLoading: false,
      statsSearchQuery: "",
      statsApplied: false,
      studentDetailsList: [],
      studentDetailsLoading: false,
      selectedAttStudent: null,
      selectedAttSubject: null,
    };
  },
  computed: {
    modalClassOptions() {
      return (this.classes || []).map((el) => el.cls).filter(Boolean);
    },
    modalSectionOptions() {
      if (
        !this.classes ||
        !this.classes.length ||
        !this.otherReportObj.classIDs ||
        !this.otherReportObj.classIDs.length
      ) {
        return [];
      }
      const selectedClassIds = this.otherReportObj.classIDs;
      let secList = [];
      this.classes.forEach((elem) => {
        if (elem.cls && selectedClassIds.includes(elem.cls.id)) {
          if (elem.sections && elem.sections.length) {
            elem.sections.forEach((s) => {
              if (!secList.some((existing) => existing.id === s.id)) {
                secList.push(s);
              }
            });
          }
        }
      });
      return secList;
    },
    modalSubjectOptions() {
      return this.subjects || [];
    },
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    isHappiness() {
      return this.reportDomain == "myskoolhappinessfoundation";
    },
    filteredStatsStudents() {
      if (!this.otherReportItems || !this.otherReportItems.length) return [];
      if (!this.statsSearchQuery) return this.otherReportItems;
      const q = this.statsSearchQuery.toLowerCase().trim();
      return this.otherReportItems.filter((std) => {
        const name = String(std.name || "").toLowerCase();
        const father = String(std.father_name || "").toLowerCase();
        const gr = String(std.grNo || "").toLowerCase();
        const cls = String(std.className || "").toLowerCase();
        const sec = String(std.sectionName || "").toLowerCase();
        return (
          name.includes(q) ||
          father.includes(q) ||
          gr.includes(q) ||
          cls.includes(q) ||
          sec.includes(q)
        );
      });
    },
  },
  created() {
    const fd = new Date();
    const firstDate = `${fd.getFullYear()}-${(fd.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-01`;
    const lastDate = `${fd.getFullYear()}-${(fd.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${new Date(fd.getFullYear(), fd.getMonth() + 1, 0)
      .getDate()
      .toString()
      .padStart(2, "0")}`;
    this.otherReportObj.dateFrom = firstDate;
    this.otherReportObj.dateTill = lastDate;

    this.LoadClasses();
    this.LoadSubjects();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    getLabel(key) {
      if (!key) return "";
      const isHappiness = this.isHappiness;
      const map = {
        class: isHappiness ? "group" : "class",
        classes: isHappiness ? "groups" : "classes",
        section: isHappiness ? "level" : "section",
        sections: isHappiness ? "levels" : "sections",
      };
      const result = map[key.toLowerCase()] ?? key.toLowerCase();
      const isCapitalized =
        key[0] === key[0].toUpperCase() && key[0] !== key[0].toLowerCase();
      return isCapitalized
        ? result.charAt(0).toUpperCase() + result.slice(1)
        : result;
    },

    async LoadClasses() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const res = await this.get(obj);
        this.classes = Array.isArray(res) ? res : [];
      } catch (e) {
        console.error("Failed to load classes", e);
      }
    },

    async LoadSubjects() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const res = await this.get(obj);
        this.subjects = Array.isArray(res) ? res : [];
      } catch (e) {
        console.error("Failed to load subjects", e);
      }
    },

    onModalClassChange() {
      if (
        this.otherReportObj.sectionIDs &&
        this.otherReportObj.sectionIDs.length
      ) {
        const validSectionIds = this.modalSectionOptions.map((s) => s.id);
        this.otherReportObj.sectionIDs = this.otherReportObj.sectionIDs.filter(
          (id) => validSectionIds.includes(id)
        );
      }
    },

    async onStatsStudentSearch(search, loading) {
      if (!search || search.trim().length < 2) {
        return;
      }
      if (typeof loading === "function") loading(true);
      this.studentSearchLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Students/Search?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&keyword=" +
            encodeURIComponent(search.trim()) +
            "&type=",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        let data = [];
        if (response && response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (Array.isArray(response)) {
          data = response;
        }
        this.searchedStudentOptions = data.map((s) => {
          let className = "";
          let sectionName = "";
          if (s.meta_class) {
            const parts = s.meta_class.split(" - ");
            className = parts[0] ? parts[0].trim() : "";
            sectionName = parts[1] ? parts[1].trim() : "";
          }
          const gr = s.grNo || s.grno || s.gr || "";
          const cls = className || s.className || "";
          const sec = sectionName || s.sectionName || "";
          const father = s.father_name || s.fatherName || "";
          let label = s.name || "";
          if (gr) label += ` (GR# ${gr})`;
          if (cls) label += ` - ${cls}${sec ? " (" + sec + ")" : ""}`;

          return {
            id: s.id,
            name: s.name || "",
            label,
            className: cls,
            sectionName: sec,
            grNo: gr,
            father_name: father,
          };
        });
      } catch (err) {
        console.error("Error searching students:", err);
      } finally {
        if (typeof loading === "function") loading(false);
        this.studentSearchLoading = false;
      }
    },

    resetStatsFilter() {
      const fd = new Date();
      const firstDate = `${fd.getFullYear()}-${(fd.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-01`;
      const lastDate = `${fd.getFullYear()}-${(fd.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${new Date(fd.getFullYear(), fd.getMonth() + 1, 0)
        .getDate()
        .toString()
        .padStart(2, "0")}`;
      this.otherReportObj = {
        dateFrom: firstDate,
        dateTill: lastDate,
        classIDs: [],
        sectionIDs: [],
        subjectIDs: [],
        studentID: null,
      };
      this.searchedStudentOptions = [];
      this.otherReportItems = [];
      this.statsApplied = false;
      this.statsSearchQuery = "";
    },

    async loadAttendanceStats() {
      if (!this.otherReportObj.dateFrom || !this.otherReportObj.dateTill) {
        this.$bvToast.toast(this.$t("Please select Date From and Date Till"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      this.statsLoading = true;
      this.statsApplied = true;
      try {
        const payload = {
          classIDs: this.otherReportObj.classIDs || [],
          sectionIDs: this.otherReportObj.sectionIDs || [],
          subjectIDs: this.otherReportObj.subjectIDs || [],
          dateFrom: this.otherReportObj.dateFrom,
          dateTill: this.otherReportObj.dateTill,
          studentID: this.otherReportObj.studentID || 0,
        };

        const response = await this.post({
          url:
            this.$store.state.domain +
            "Attendance/LoadStats?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&cID=" +
            this.$store.state.userData.cId,
          body: payload,
          subjects: true,
          token: this.$store.state.userData.token,
        });

        this.otherReportItems = Array.isArray(response) ? response : [];
        if (!this.otherReportItems.length) {
          this.$bvToast.toast(
            this.$t("No attendance records found for the selected filter"),
            {
              title: this.$t("Info"),
              variant: "info",
              toaster: "b-toaster-top-center",
            }
          );
        }
      } catch (err) {
        this.$bvToast.toast(this.$t("Failed to load attendance summary"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.statsLoading = false;
      }
    },

    async showAttDetails(std, sub) {
      this.selectedAttStudent = std || null;
      this.selectedAttSubject = sub || null;
      this.studentDetailsList = [];
      this.studentDetailsLoading = true;
      this.$bvModal.show("modal-att-details");

      try {
        let classIDs = [];
        if (std && (std.classID || std.clsID || std.cID)) {
          classIDs = [std.classID || std.clsID || std.cID];
        } else if (
          this.otherReportObj.classIDs &&
          this.otherReportObj.classIDs.length
        ) {
          classIDs = this.otherReportObj.classIDs;
        }

        let sectionIDs = [];
        if (std && (std.sectionID || std.secID || std.section)) {
          sectionIDs = [std.sectionID || std.secID || std.section];
        } else if (
          this.otherReportObj.sectionIDs &&
          this.otherReportObj.sectionIDs.length
        ) {
          sectionIDs = this.otherReportObj.sectionIDs;
        }

        let subjectIDs = [];
        if (sub && (sub.subID || sub.id)) {
          subjectIDs = [sub.subID || sub.id];
        } else if (
          this.otherReportObj.subjectIDs &&
          this.otherReportObj.subjectIDs.length
        ) {
          subjectIDs = this.otherReportObj.subjectIDs;
        }

        const payload = {
          classIDs: classIDs,
          sectionIDs: sectionIDs,
          subjectIDs: subjectIDs,
          dateFrom: this.otherReportObj.dateFrom,
          dateTill: this.otherReportObj.dateTill,
          studentID:
            std && (std.sID || std.id)
              ? std.sID || std.id
              : this.otherReportObj.studentID || 0,
        };

        const response = await this.post({
          url:
            this.$store.state.domain +
            "Attendance/LoadStudentDetails?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&cID=" +
            this.$store.state.userData.cId,
          body: payload,
          subjects: true,
          token: this.$store.state.userData.token,
        });

        if (Array.isArray(response)) {
          this.studentDetailsList = response;
        } else if (response && typeof response === "object") {
          this.studentDetailsList = [response];
        } else {
          this.studentDetailsList = [];
        }
      } catch (err) {
        this.$bvToast.toast(
          this.$t("Failed to load student attendance details"),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
      } finally {
        this.studentDetailsLoading = false;
      }
    },

    getAttStatusVariant(status) {
      if (!status) return "light-secondary";
      const s = String(status).trim().toLowerCase();
      if (s === "p" || s === "present") return "light-success";
      if (s === "late") return "light-warning";
      if (s === "very late" || s === "verylate" || s === "vl")
        return "light-warning";
      if (s === "a" || s === "absent") return "light-danger";
      if (s === "l" || s === "leave" || s === "e" || s === "excused")
        return "light-info";
      if (s === "h" || s === "holiday") return "light-secondary";
      return "light-primary";
    },

    getAttStatusStyle(status) {
      if (!status) return "";
      const s = String(status).trim().toLowerCase();
      if (s === "late") {
        return "color: #b78103; background-color: rgba(255, 193, 7, 0.15);";
      }
      if (s === "very late" || s === "verylate" || s === "vl") {
        return "color: #ff9f43; background-color: rgba(255, 159, 67, 0.15);";
      }
      return "";
    },

    formatAttStatus(status) {
      if (!status) return "-";
      const s = String(status).trim().toLowerCase();
      if (s === "p" || s === "present") return "Present";
      if (s === "late") return "Late";
      if (s === "very late" || s === "verylate" || s === "vl")
        return "Very Late";
      if (s === "a" || s === "absent") return "Absent";
      if (s === "l" || s === "leave" || s === "e" || s === "excused")
        return "Excused";
      if (s === "h" || s === "holiday") return "Holiday";
      return status;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style scoped>
.student-stats-card {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
}

.student-stats-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08) !important;
}

.stats-search-input {
  border-radius: 6px;
}
</style>
